import React from 'react';
import styleable from 'react-styleable';
import PropTypes from 'prop-types';
import Loader from 'halogen/PulseLoader';
import { injectIntl, intlShape } from 'react-intl';
import cloneDeep from 'lodash/cloneDeep';
import css from './index.scss';
import ClassHeader from '../class-header/index';
import ClassFooter from '../class-footer/index';
import DataGrid from '../../components/data-grid/index';
import { ClassRowRenderer } from '../../components/renderers/index';
import ClassFilter from '../../components/class-filter/index';
import EmptyState from '../../components/empty-state/index';
import * as gridFormatters from '../../components/formatters/index';
import { StudentManagement } from '../../helpers/icon-dictionary';
import Messages from './messages';
import AddClass from '../../components/add-class/index';
import productEnum from '../../helpers/product-enum';

function getGridColumns(handleGridSort, handleOnClassEdit, handleOnAssignTeacher, handleOnAssignStudent, intl, selectedProduct) {
    const courseColumn = [];

    if (selectedProduct.id === productEnum.MATHLETICS.id) {
        courseColumn.push({
            key: 'courses',
            name: `${intl.formatMessage(Messages.learningModules)}`,
            resizable: true,
            getRowMetaData: row => row,
            formatter: <gridFormatters.AssignedCoursesFormatter />
        });
    } else {
        courseColumn.push({
            key: 'courseTitle',
            name: `${intl.formatMessage(Messages.learningModules)}`,
            resizable: true,
            sortable: true,
            getRowMetaData: row => row,
            headerRenderer: <gridFormatters.HeaderFormatter onSort={handleGridSort} />
        });
    }

    return [
        {
            key: 'name',
            name: `${intl.formatMessage(Messages.class)}`,
            resizable: true,
            sortable: true,
            headerRenderer: <gridFormatters.HeaderFormatter onSort={handleGridSort} />
        },
        {
            key: 'yearGroupEnum',
            name: `${intl.formatMessage(Messages.year)}`,
            sortable: true,
            width: 70,
            formatter: gridFormatters.YearGroupFormatter,
            getRowMetaData: row => row,
            headerRenderer: <gridFormatters.HeaderFormatter onSort={handleGridSort} />
        },
        ...courseColumn,
        {
            key: 'teachers',
            name: `${intl.formatMessage(Messages.teacher)}`,
            resizable: true,
            getRowMetaData: row => row,
            formatter: <gridFormatters.AssignedTeacherFormatter handleOnAssignTeacher={handleOnAssignTeacher} />
        },
        {
            key: 'studentsCount',
            name: `${intl.formatMessage(Messages.students)}`,
            width: 180,
            getRowMetaData: row => row,
            formatter: <gridFormatters.StudentCountFormatter handleOnAssignStudents={handleOnAssignStudent} />
        },
        {
            key: 'edit',
            name: `${intl.formatMessage(Messages.edit)}`,
            width: 70,
            locked: true,
            getRowMetaData: row => row,
            formatter: <gridFormatters.EditInfoFormatter handleOnEdit={handleOnClassEdit} />
        }
    ];
}

const ClassRoll = (props) => {
    const {
        addClass,
        classrooms,
        classesCount,
        filteredClassesCount,
        selectedClasses = [],
        isClassroomDataLoaded,
        isClassRollDownloaded,
        showAddClassConfirmationModal,
        showAddClassValidationModal,
        addClassValidationMessage,
        closeAddStudentValidationModal,
        showAddClassModal,
        onDownloadClassRoll,
        onQuickUpload,
        openAddClassModal,
        handleOnDownloadStudentsSignInCards,
        isStudentsSignInCardDownloaded,
        isProductCoursesFetching,
        isProductCoursesLoaded,
        isSaveInProgress,
        isSchoolCoursesFetching,
        gridRowGetter,
        onGridSort,
        onRowSelect,
        handleOnClassEdit,
        intl,
        classModel,
        onAddClassCloseModal,
        handleOnAddClassSubmit,
        selectedProduct,
        yearGroups,
        productCountries,
        productCourses,
        productTeachers,
        handleListOnChange,
        dataFilterOptions,
        filterOnChange,
        filterOnClear,
        onSearch,
        handleOnAssignTeacher,
        handleOnAssignStudent,
        getProductCourses,
        getSchoolCourses,
        handleOnDeleteClassrooms,
        deletingClassrooms,
        showDeleteClassroomsConfirmation,
        showDeleteClassroomsSuccess,
        handleOnCloseDeleteClassroomsConfirmation,
        handleOnCloseDeleteClassroomsSuccess,
        handleOnShowDeleteClassroomsConfirmation,
        deletingEmptyClassrooms,
        handleOnStudentSignInCards,
        handleOnCancelStudentSignInCards,
        showHideSignInCardModal,
        getFilteredUPFCourses,
        getFilteredNewCourses,
        upfCourses,
        newCourses,
        clearProductCourses,
        clearUPFCourses,
        isUPFCoursesFetching,
        isNewCoursesFetching,
        displayNewCourses,
        upfCountries
    } = props;

    const loaderView = (
        <div className={css.classListContainer}>
            <div className={css.loadingContainer}>
                <Loader className={css.loader} />
            </div>
        </div>);

    const emptyStateView = (
        <div className={css.classListContainer}>
            <ClassFilter
                handleFilterOnChange={filterOnChange}
                handleFilterOnClear={filterOnClear}
                handleOnSearch={onSearch}
                searchTerm={dataFilterOptions.searchTerm}
                teachers={dataFilterOptions.teachers}
                yearGroups={dataFilterOptions.yearGroups} />
            <EmptyState iconName={StudentManagement} message={intl.formatMessage(Messages.noClassesAvailable)} />
        </div>);

    const dataView = (
        <div className={css.classListContainer}>
            <ClassFilter
                handleFilterOnChange={filterOnChange}
                handleFilterOnClear={filterOnClear}
                handleOnSearch={onSearch}
                searchTerm={dataFilterOptions.searchTerm}
                teachers={dataFilterOptions.teachers}
                yearGroups={dataFilterOptions.yearGroups} />
            <DataGrid
                columns={getGridColumns(onGridSort, handleOnClassEdit, handleOnAssignTeacher, handleOnAssignStudent, intl, selectedProduct)}
                handleGridSort={onGridSort}
                handleRowSelect={onRowSelect}
                overScan={{ colsStart: 17, colsEnd: 17, rowsStart: 25, rowsEnd: 25 }}
                rowGetter={gridRowGetter}
                rowHeight={150}
                rowKey="classroomId"
                rowRenderer={ClassRowRenderer}
                rowsCount={filteredClassesCount}
                selectedRows={selectedClasses.length > 0 ? selectedClasses : []}
            />
        </div>);

    const showAddClass = showAddClassModal || showAddClassConfirmationModal;
    const loadAddClassModalComponent = () => {
        const addClassYearGroups = cloneDeep(yearGroups);

        const addClassProps = {
            addClass,
            addClassValidationMessage,
            classroom: classModel,
            closeAddStudentValidationModal,
            getProductCourses,
            getSchoolCourses,
            handleListOnChange,
            handleOnSubmit: handleOnAddClassSubmit,
            isActive: showAddClass,
            isProductCoursesFetching,
            isProductCoursesLoaded,
            isSaveInProgress,
            isSchoolCoursesFetching,
            onAddClassCloseModal,
            productCountries,
            productCourses,
            productTeachers,
            selectedProduct,
            showAddClassConfirmationModal,
            showAddClassValidationModal,
            yearGroups: addClassYearGroups,
            getFilteredUPFCourses,
            getFilteredNewCourses,
            upfCourses,
            newCourses,
            clearProductCourses,
            clearUPFCourses,
            isUPFCoursesFetching,
            isNewCoursesFetching,
            displayNewCourses,
            upfCountries
        };
        return (<AddClass {...addClassProps} />);
    };

    const addClassModalView = showAddClass ? (
        loadAddClassModalComponent()
    ) : '';

    let finalView;
    if (!isClassroomDataLoaded) {
        finalView = loaderView;
    } else {
        finalView = filteredClassesCount === 0 ? emptyStateView : dataView;
    }

    const classesComponent = (
        <div className={css.classRollComponent}>
            <ClassHeader
                classesCountInSchool={classesCount}
                classroomsLength={classrooms.length}
                filteredClassesCount={filteredClassesCount}
                handleAddClass={openAddClassModal}
                handleOnDownloadClassRoll={onDownloadClassRoll}
                handleQuickUpload={onQuickUpload}
                isClassRollDownloaded={isClassRollDownloaded}
                isClassroomDataLoaded={isClassroomDataLoaded}
                selectedProduct={selectedProduct} />
            {finalView}
            <ClassFooter
                deletingClassrooms={deletingClassrooms}
                deletingEmptyClassrooms={deletingEmptyClassrooms}
                handleOnCancelStudentSignInCards={handleOnCancelStudentSignInCards}
                handleOnCloseDeleteClassroomsConfirmation={handleOnCloseDeleteClassroomsConfirmation}
                handleOnCloseDeleteClassroomsSuccess={handleOnCloseDeleteClassroomsSuccess}
                handleOnDeleteClassrooms={handleOnDeleteClassrooms}
                handleOnDownloadStudentsSignInCards={handleOnDownloadStudentsSignInCards}
                handleOnShowDeleteClassroomsConfirmation={handleOnShowDeleteClassroomsConfirmation}
                handleOnStudentSignInCards={handleOnStudentSignInCards}
                isStudentsSignInCardDownloaded={isStudentsSignInCardDownloaded}
                noOfSelectedClassrooms={selectedClasses.length}
                showDeleteClassroomsConfirmation={showDeleteClassroomsConfirmation}
                showDeleteClassroomsSuccess={showDeleteClassroomsSuccess}
                showHideSignInCardModal={showHideSignInCardModal}
            />
        </div>);

    return (
        <div className={css.classRollComponent}>
            { classesComponent }
            { addClassModalView }
        </div>
    );
};

ClassRoll.propTypes = {
    addClass: PropTypes.func,
    classrooms: PropTypes.arrayOf(PropTypes.object),
    selectedClasses: PropTypes.arrayOf(PropTypes.number),
    classesCount: PropTypes.number,
    filteredClassesCount: PropTypes.number,
    isClassroomDataLoaded: PropTypes.bool,
    isClassRollDownloaded: PropTypes.bool,
    onDownloadClassRoll: PropTypes.func,
    onQuickUpload: PropTypes.func,
    openAddClassModal: PropTypes.func,
    handleOnDownloadStudentsSignInCards: PropTypes.func,
    isStudentsSignInCardDownloaded: PropTypes.bool,
    isProductCoursesFetching: PropTypes.bool,
    isSchoolCoursesFetching: PropTypes.bool,
    isProductCoursesLoaded: PropTypes.bool,
    isSaveInProgress: PropTypes.bool,
    intl: intlShape.isRequired,
    handleOnClassEdit: PropTypes.func,
    gridRowGetter: PropTypes.func,
    onGridSort: PropTypes.func,
    onRowSelect: PropTypes.func,
    classModel: PropTypes.object,
    showAddClassConfirmationModal: PropTypes.bool,
    showAddClassValidationModal: PropTypes.bool,
    addClassValidationMessage: PropTypes.string,
    closeAddStudentValidationModal: PropTypes.func,
    showAddClassModal: PropTypes.bool,
    onAddClassCloseModal: PropTypes.func,
    handleOnAddClassSubmit: PropTypes.func,
    selectedProduct: PropTypes.object,
    yearGroups: PropTypes.arrayOf(PropTypes.object),
    productCountries: PropTypes.arrayOf(PropTypes.object),
    upfCountries: PropTypes.arrayOf(PropTypes.object),
    productCourses: PropTypes.arrayOf(PropTypes.object),
    productTeachers: PropTypes.arrayOf(PropTypes.object),
    handleListOnChange: PropTypes.func,
    dataFilterOptions: PropTypes.object,
    filterOnChange: PropTypes.func,
    filterOnClear: PropTypes.func,
    onSearch: PropTypes.func,
    handleOnAssignTeacher: PropTypes.func,
    handleOnAssignStudent: PropTypes.func,
    getProductCourses: PropTypes.func,
    getSchoolCourses: PropTypes.func,
    handleOnDeleteClassrooms: PropTypes.func,
    deletingClassrooms: PropTypes.bool,
    showDeleteClassroomsConfirmation: PropTypes.bool,
    showDeleteClassroomsSuccess: PropTypes.bool,
    handleOnCloseDeleteClassroomsConfirmation: PropTypes.func,
    handleOnCloseDeleteClassroomsSuccess: PropTypes.func,
    handleOnShowDeleteClassroomsConfirmation: PropTypes.func,
    deletingEmptyClassrooms: PropTypes.bool,
    handleOnStudentSignInCards: PropTypes.func,
    handleOnCancelStudentSignInCards: PropTypes.func,
    showHideSignInCardModal: PropTypes.bool,
    getFilteredUPFCourses: PropTypes.func,
    getFilteredNewCourses: PropTypes.func,
    upfCourses: PropTypes.array,
    newCourses: PropTypes.array,
    clearProductCourses: PropTypes.func,
    clearUPFCourses: PropTypes.func,
    isUPFCoursesFetching: PropTypes.bool,
    isNewCoursesFetching: PropTypes.bool,
    displayNewCourses: PropTypes.bool
};

export default injectIntl(styleable(css)(ClassRoll));



// WEBPACK FOOTER //
// ./app/components/class-roll/index.js